import { Loader2, MapPin } from "lucide-react";
import { useState, useRef } from "react";
import { useAddressSearch } from "@/shared/hooks/useAddressSearch";
import { cn } from "@/shared/lib/utils";
import type { AddressSuggestion } from "@/shared/services/address.service";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/shared/ui/Command";
import { Input } from "@/shared/ui/Input";
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from "@/shared/ui/Popover";

type AddressAutocompleteProps = {
  value: string
  onChange: (value: string) => void
  onAddressSelect: (address: AddressSuggestion) => void
  placeholder?: string
  disabled?: boolean
  className?: string
};

const AddressAutocomplete = ({
  value,
  onChange,
  onAddressSelect,
  placeholder = "Rechercher une adresse...",
  disabled = false,
  className,
}: AddressAutocompleteProps) => {
  const [open, setOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { suggestions, isLoading } = useAddressSearch(isTyping ? value : "");

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setIsTyping(true);

    if (newValue.length >= 3) {
      setOpen(true);
    }
    else {
      setOpen(false);
    }
  };

  const handleSelect = (address: AddressSuggestion) => {
    setIsTyping(false);
    setOpen(false);
    onAddressSelect(address);
  };

  const handleInputBlur = () => {
    // Délai pour permettre le clic sur une suggestion
    setTimeout(() => {
      setOpen(false);
    }, 150);
  };

  const showSuggestions = open && suggestions.length > 0;

  return (
    <Popover open={showSuggestions} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className="relative">
          <Input
            ref={inputRef}
            value={value}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            placeholder={placeholder}
            disabled={disabled}
            name={`search-${Math.random().toString(36).slice(2)}`}
            autoComplete="new-password"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            data-form-type="other"
            data-lpignore="true"
            data-1p-ignore="true"
            className={cn("pr-8", className)}
          />
          {isLoading && (
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command>
          <CommandList>
            <CommandEmpty>Aucune adresse trouvée.</CommandEmpty>
            <CommandGroup>
              {suggestions.map((address) => (
                <CommandItem
                  key={address.id}
                  value={address.id}
                  onSelect={() => handleSelect(address)}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="truncate">{address.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default AddressAutocomplete;
